import { type api, useApiQuery, useTeam } from '@vrplatform/data';
import { day, formatCurrency, sum } from '@vrplatform/utils';
import { useMemo } from 'react';
import { useTrialBalanceTableFilter } from './useTrialBalanceTableFilter';

export const STORYBOOK_TRIAL_BALANCE_TABLE_QUERY_KEY = 'trial-balance-table';

export type TrialBalanceRow = NonNullable<
  ReturnType<typeof useTrialBalanceTableQuery>['data']
>['list'][number];

export const getTrialBalanceQueryFilterParams = (
  args: Omit<ReturnType<typeof useTrialBalanceTableFilter>['filter'], 'view'>
): api['/reports/trial-balance']['get']['parameters']['query'] => {
  const [start, end] = (args.date || '').split('...');

  return {
    startDate: start,
    endDate: end
      ? day(end).add(1, 'day').yyyymmdd()
      : day(start).add(1, 'day').yyyymmdd(),
    ledger: args.ledger ?? undefined,
    classification: args.classifications?.length
      ? args.classifications?.join(',')
      : undefined,
    party: args.party || undefined,
    type: args.type || undefined,
    search: args.search?.trim() || undefined,
  };
};

export type TrialBalanceQueryData = NonNullable<
  ReturnType<typeof useTrialBalanceTableQuery>['data']
>;

export const useTrialBalanceTableQuery = () => {
  const [{ currency, id: teamId }] = useTeam();

  const { filter } = useTrialBalanceTableFilter();

  const queryData = useApiQuery(
    async (api, signal, args) => {
      const [start] = (args.filter.date || '').split('...');
      if (!start) throw new Error('A date is required');
      const result = await api.GET('/reports/trial-balance', {
        params: {
          query: getTrialBalanceQueryFilterParams(args.filter),
        },
        signal,
      });

      if (result.error) throw result.error;

      type UUID = `${string}-${string}-${string}-${string}-${string}`;

      type Row = {
        id: UUID | 'unmapped';
        title: string;
        type: string | undefined;
        classification: string | undefined;
        debit: number;
        credit: number;
        balanceStart: number | undefined;
        balanceEnd: number | undefined;
      };

      const nonAssigned: Row = {
        id: 'unmapped',
        title: 'Unassigned',
        classification: undefined,
        type: undefined,
        balanceEnd: result.data.unassignedBalanceEnd,
        balanceStart: result.data.unassignedBalanceStart,
        credit: result.data.unassignedCredit,
        debit: result.data.unassignedDebit,
      };

      const accounts = result.data.accounts.map<Row>((account) => {
        return {
          id: account.id as UUID,
          title: account.title,
          type: undefined,
          classification: account.classification || undefined,
          credit: account.credit,
          debit: account.debit,
          balanceStart: account.balanceStart,
          balanceEnd: account.balanceEnd,
        };
      });

      const list = nonAssigned ? [nonAssigned, ...accounts] : accounts;

      return {
        list,
        totals: {
          balanceStart: formatCurrency(
            sum(list.map((x) => (x.balanceStart ?? 0) / 100)),
            currency
          ),
          debit: formatCurrency(sum(list.map((x) => x.debit / 100)), currency),
          credit: formatCurrency(
            sum(list.map((x) => x.credit / 100)),
            currency
          ),
          balanceEnd: formatCurrency(
            sum(list.map((x) => (x.balanceEnd ?? 0) / 100)),
            currency
          ),
        },
      };
    },
    {
      variables: {
        filter,
        teamId,
      },
      storybookQueryKey: STORYBOOK_TRIAL_BALANCE_TABLE_QUERY_KEY,
      queryGroup: 'reports',
      uniqueQueryKey: 'trial-balance',
    }
  );

  const data = useMemo(() => {
    if (filter.view === 'non-zero' && queryData.data) {
      return {
        ...queryData.data,
        list: queryData.data.list.filter((row) => {
          const isZero =
            row.debit === 0 &&
            row.credit === 0 &&
            row.balanceStart === 0 &&
            row.balanceEnd === 0;

          return !isZero;
        }),
      };
    }

    return queryData.data;
  }, [queryData.data, filter.view]);

  return {
    ...queryData,
    data,
  };
};
